<!--
  LIST «перечень» table block on passport v2: admin-like chrome, session-local summary toggle,
  table rows + summary cards (values from API when available).
-->
<script setup lang="ts">
import { computed, ref, toRef, watch } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useQueryClient } from '@tanstack/vue-query'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import type { GridFieldType, SummaryLayoutItem } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import { packSummaryLayoutRows } from '@/features/Inventory/PassportBuilderV2/model/tableLayout'
import { formatInventoryObjectNames, useTranslate } from '@/shared/lib'
import { useConfirm, VIcons } from '@/shared/ui'
import { PB_TABLE_NAME_SEGMENT_ID, resolvedTableSegments, type ResolvedTableSegment } from '../model/runtimeTableLayout'
import { resolveChildPassportLocation } from '../model/passportRuntimeChildRoute'
import {
  invalidatePassportRuntimeListTableQueries,
  useTableBindingMutations,
} from '../model/useTableBindingMutations'
import {
  usePassportRuntimeListTable,
  type PassportRuntimeListTableRow,
} from '../model/usePassportRuntimeListTable'
import { resolveListGroupListInventTypeId } from '../model/resolveListGroupListInventTypeId'
import PassportRuntimeLinkTableModal from './PassportRuntimeLinkTableModal.vue'
import { navigateToMapView, navigateToPlaceOnMap } from '@/features/Inventory/PlaceOnMap'
import { usePendingTableBindingsOptional } from '../model/usePendingTableBindings'

const INNER_COLS = 12
const ACTIONS_COL_SPAN = 1

const props = withDefaults(
  defineProps<{
    layoutItem: GridFieldType
    objectId: string
    elementId: number
    /**
     * When true, only the orange LIST chrome (title bar) is shown — no table body, summary, or API loads.
     * Used by Create V2 batch staging (children are edited as separate draft nodes).
     */
    headerOnly?: boolean
    /** Historical snapshot filter; passed to linked-row loads. */
    filter?: { status_date: string | null }
    /** Optional catalog fields to resolve summary card labels from `ref_id`. */
    allFields?: ObjectInventoryField[]
    /** When true (version snapshot), row delete is disabled. */
    fieldsLocked?: boolean
    /** Type `field_groups` / `groups` from object type — used when `layoutItem.inventory_type` is missing. */
    fieldGroups?: unknown[]
    /** Passport API `stats` — used to derive list rows from `by_type` when `table-bound-passports` is empty. */
    stats?: unknown
  }>(),
  {
    headerOnly: false,
    filter: () => ({ status_date: null }),
    allFields: () => [],
    fieldsLocked: false,
    fieldGroups: () => [],
    stats: undefined,
  },
)

const { trs } = useTranslate()
const route = useRoute()
const router = useRouter()
const { show } = useConfirm()
const queryClient = useQueryClient()
const { postDeleteChild, isPending: bindingPending } = useTableBindingMutations()
const pendingBindings = usePendingTableBindingsOptional()

const showSummary = ref(!!props.layoutItem.summary_mode)
const linkModalOpen = ref(false)
const page = ref(1)
const itemsPerPage = ref(10)

watch(
  () => props.layoutItem.id,
  () => {
    showSummary.value = !!props.layoutItem.summary_mode
  },
)

const layoutItemRef = toRef(props, 'layoutItem')
/** Route `object_id` — map group for `/inventory/objects/{id}/passport/...`. */
const inventoryGroupObjectIdRef = toRef(props, 'objectId')
/** Route `invent_object_id` — parent row `object_list_id` for query `object_id`. Empty when `headerOnly` disables fetches. */
const passportObjectListIdRef = computed(() => (props.headerOnly ? '' : String(props.elementId ?? '')))
const filterRef = toRef(props, 'filter')
const allFieldsRef = toRef(props, 'allFields')
const fieldGroupsRef = toRef(props, 'fieldGroups')
const statsRef = toRef(props, 'stats')
const {
  rows: tableRows,
  isLoading: tableLoading,
  isError: tableError,
  refetch: refetchTable,
  summaryValues,
  summaryLoading,
  summaryError,
  refetchSummary: refetchSummaryQuery,
  summaryApiEnabled,
  coordsPresent,
} = usePassportRuntimeListTable({
  layoutItem: layoutItemRef,
  inventoryGroupObjectId: inventoryGroupObjectIdRef,
  passportObjectListId: passportObjectListIdRef,
  filter: filterRef,
  allFields: allFieldsRef,
  fieldGroups: fieldGroupsRef,
  stats: statsRef,
})

const blockTitle = computed(() => props.layoutItem.name?.trim() || '—')

const segments = computed((): ResolvedTableSegment[] => resolvedTableSegments(props.layoutItem))

const summaryRows = computed(() => packSummaryLayoutRows(props.layoutItem.summary_layout ?? []))

const summaryIsEmpty = computed(() => !(props.layoutItem.summary_layout?.length ?? 0))

const listGroupId = computed(() => {
  const n = Number(props.layoutItem.list_group_id ?? 0)
  return Number.isFinite(n) ? n : 0
})

const inventTypeId = computed(() => {
  const n = Number(props.layoutItem.inventory_type ?? 0)
  if (Number.isFinite(n) && n > 0) return n
  return resolveListGroupListInventTypeId(listGroupId.value, props.fieldGroups)
})

const tableFieldIdStr = computed(() => String(props.layoutItem.id ?? ''))

/** Отложенные операции для этой таблицы: новые строки + помеченные на отвязку. */
const pendingForThis = computed(() => pendingBindings.pendingForTable(listGroupId.value, tableFieldIdStr.value))

const removedChildIds = computed(
  () => new Set(pendingForThis.value.removed.map((o) => o.child_object_list_id)),
)

const pendingLinkRows = computed(() =>
  pendingForThis.value.added.map((op) => ({
    childElementId: op.child_object_list_id,
    name: op.name ?? '—',
    columnCells: {} as Record<string, string>,
    coordinates: undefined,
    __pending: 'link' as const,
    __pendingKey: op.key,
  })),
)

const mergedRows = computed(() => {
  const rem = removedChildIds.value
  const base = tableRows.value.filter((r) => !rem.has(r.childElementId))
  return [...pendingLinkRows.value, ...base]
})

const totalRows = computed(() => mergedRows.value.length)
const totalPages = computed(() => Math.ceil(totalRows.value / itemsPerPage.value) || 1)
const paginatedRows = computed(() => {
  if (totalRows.value === 0) return []
  const start = (page.value - 1) * itemsPerPage.value
  return mergedRows.value.slice(start, start + itemsPerPage.value)
})
const paginationStart = computed(() => (page.value - 1) * itemsPerPage.value + 1)
const paginationEnd = computed(() => Math.min(page.value * itemsPerPage.value, totalRows.value))

watch(
  () => mergedRows.value.length,
  () => { page.value = 1 },
)
watch(itemsPerPage, () => {
  const maxPage = Math.ceil(totalRows.value / itemsPerPage.value) || 1
  if (page.value > maxPage) page.value = maxPage
})

const boundChildIds = computed(() => {
  const server = tableRows.value.map((r) => r.childElementId).filter((id) => id > 0)
  const pending = pendingForThis.value.added.map((o) => o.child_object_list_id)
  return [...server, ...pending]
})

/** Link modal: always allowed when not locked and has valid LIST ids. */
const canLink = computed(() => {
  if (props.fieldsLocked) return false
  if (listGroupId.value <= 0 || inventTypeId.value <= 0) return false
  if (!String(props.layoutItem.id ?? '').trim()) return false
  return true
})

function segmentHeaderLabel(seg: ResolvedTableSegment): string {
  if (seg.kind === 'name') return trs('itf.name_label')
  return seg.column.name?.trim() || '—'
}

function fieldLabelForRef(refId: string | number): string | undefined {
  const list = props.allFields ?? []
  const key = String(refId)
  const inv = list.find((f) => String(f.id) === key)
  return inv?.name?.trim()
}

function summaryCardLabel(item: SummaryLayoutItem): string {
  const explicit = item.label?.trim()
  if (explicit) return explicit
  if (item.kind === 'heading') return String(item.ref_id ?? '').trim() || '—'
  return fieldLabelForRef(item.ref_id) ?? String(item.ref_id ?? '—')
}

function summaryCardValue(item: SummaryLayoutItem): string {
  if (!summaryApiEnabled.value) return '—'
  const key = String(item.id)
  const v = summaryValues.value[key]
  if (v !== undefined && v !== '') return v
  if (summaryLoading.value) return '…'
  if (summaryError.value) return '—'
  return '—'
}

function childPassportTo(rowId: number) {
  return resolveChildPassportLocation(route, router, rowId)
}

function cellTextForSegment(row: PassportRuntimeListTableRow, seg: ResolvedTableSegment): string {
  if (seg.kind === 'name') return row.name
  return row.columnCells[String(seg.column.id)] ?? '—'
}

function canDeleteRow(_childId: number): boolean {
  if (props.fieldsLocked) return false
  return true
}

function deleteTooltip(childId: number): string {
  if (props.fieldsLocked) return trs('itf.passport_runtime_list_delete_disabled_locked')
  if (!canDeleteRow(childId)) return trs('itf.passport_runtime_list_delete_disabled')
  return trs('itf.archive')
}

function editTooltip(): string {
  if (props.fieldsLocked) return trs('itf.passport_runtime_list_edit_disabled_locked')
  return trs('itf.passport_runtime_list_action_edit')
}

function mapTooltip(hasGeom: boolean): string {
  if (!hasGeom) return trs('itf.place_on_map')
  return trs('itf.passport_runtime_list_action_map')
}

function onMapRowAction(childId: number, hasGeom: boolean) {
  if (hasGeom) {
    navigateToMapView(router, route, childId)
  } else {
    navigateToPlaceOnMap(router, route, childId)
  }
}

function onDeleteRow(childId: number, name?: string) {
  if (!canDeleteRow(childId) || props.fieldsLocked) return
  show({
    title: trs('itf.inventory_object_delete', {
      length: 1,
      names: formatInventoryObjectNames([{ id: childId, name }]),
    }),
    confirmFunc: async () => {
      const ok = await postDeleteChild(props.objectId, childId)
      if (!ok) return
      await invalidatePassportRuntimeListTableQueries(queryClient, props.objectId)
    },
  })
}

function onUnlinkRow(childId: number, name?: string) {
  if (props.fieldsLocked) return
  pendingBindings.stageUnlink({
    list_group_id: listGroupId.value,
    table_field_id: tableFieldIdStr.value,
    child_object_list_id: childId,
    name,
  })
}

/** Состояние строки относительно отложенных операций: новая / будет отвязана / нет. */
function pendingStateFor(childId: number): { kind: 'link' | 'unlink'; key?: string } | null {
  const added = pendingForThis.value.added.find((o) => o.child_object_list_id === childId)
  if (added) return { kind: 'link', key: added.key }
  if (removedChildIds.value.has(childId)) {
    const rem = pendingForThis.value.removed.find((o) => o.child_object_list_id === childId)
    return { kind: 'unlink', key: rem?.key }
  }
  return null
}

function onUndoPending(key?: string) {
  if (key) pendingBindings.undo(key)
}

/** CSS-модификатор строки по её отложенному состоянию. */
function rowPendingClass(childId: number): string {
  const st = pendingStateFor(childId)
  if (st?.kind === 'link') return 'prt-list-table__tr--pending-link'
  if (st?.kind === 'unlink') return 'prt-list-table__tr--pending-unlink'
  return ''
}


function onRetryTable() {
  refetchTable()
}

function onRetrySummary() {
  refetchSummaryQuery()
}

function onCreateNew() {
  const url = router.resolve({
    name: 'object.inventory.type.elements.create-v2',
    params: {
      invent_type_id: String(inventTypeId.value),
      tab_inventory: 'elements',
    },
    query: {
      parent_id: String(props.elementId),
      ...(props.layoutItem.list_group_id != null
        ? { list_group_id: String(props.layoutItem.list_group_id) }
        : {}),
    },
  })
  window.open(url.fullPath, '_blank', 'noopener,noreferrer')
}

async function onLinkAttached() {
  // Table rows already refreshed by invalidatePassportRuntimeListTableQueries in the modal.
}
</script>

<template>
  <div
    class="prt-list-table"
    :data-runtime-table-id="String(layoutItem.id)"
    :data-object-id="objectId"
    :data-element-id="String(elementId)"
    :data-status-date="filter?.status_date ?? ''"
  >
    <div class="prt-list-table__header">
      <VIcons type="table_chart" class="prt-list-table__header-icon" aria-hidden="true" />
      <span class="prt-list-table__header-title">{{ blockTitle }}</span>
      <div v-if="!headerOnly" class="prt-list-table__header-tools">
        <VTooltip location="top">
          <template #activator="{ props: tip }">
            <span v-bind="tip" class="prt-list-table__header-action-wrap">
              <VBtn
                icon
                variant="text"
                density="compact"
                class="prt-list-table__header-tool-btn"
                :disabled="!canLink"
                :aria-label="trs('itf.passport_runtime_link_existing_tooltip')"
                @click="linkModalOpen = true"
              >
                <VIcons type="link" />
              </VBtn>
            </span>
          </template>
          <span>{{ trs('itf.passport_runtime_link_existing_tooltip') }}</span>
        </VTooltip>

        <VBtn
          variant="text"
          density="compact"
          class="prt-list-table__header-tool-btn"
          :disabled="fieldsLocked"
          @click="onCreateNew"
        >
          Создать
        </VBtn>

        <VSwitch
          v-model="showSummary"
          class="prt-list-table__switch"
          :label="trs('itf.passport_runtime_list_summary_mode')"
          hide-details
          density="compact"
        />

      </div>
    </div>

    <div v-if="!headerOnly && showSummary" class="prt-list-table__body prt-list-table__body--summary" role="region">
      <template v-if="summaryIsEmpty">
        <p class="prt-list-table__summary-empty">{{ trs('itf.passport_runtime_list_summary_not_configured') }}</p>
        <p class="prt-list-table__summary-hint">{{ trs('itf.passport_runtime_list_summary_switch_hint') }}</p>
      </template>
      <template v-else>
        <div
          v-if="summaryApiEnabled && summaryLoading"
          class="prt-list-table__inline-state"
        >
          {{ trs('itf.passport_runtime_list_summary_loading') }}
        </div>
        <div
          v-else-if="summaryApiEnabled && summaryError"
          class="prt-list-table__inline-state prt-list-table__inline-state--error"
        >
          <span>{{ trs('itf.passport_runtime_list_summary_error') }}</span>
          <VBtn variant="text" size="small" density="compact" @click="onRetrySummary">
            {{ trs('itf.passport_runtime_list_retry') }}
          </VBtn>
        </div>
        <div
          v-for="(row, ri) in summaryRows"
          :key="`sum-row-${ri}`"
          class="prt-list-table__summary-row"
          :style="{ gridTemplateColumns: `repeat(${INNER_COLS}, minmax(0, 1fr))` }"
        >
          <template v-for="item in row" :key="item.id">
            <div
              v-if="item.kind === 'heading'"
              class="prt-list-table__summary-heading"
              :style="{ gridColumn: `span ${Math.min(INNER_COLS, Math.max(1, item.length))}` }"
            >
              {{ summaryCardLabel(item) }}
            </div>
            <div
              v-else
              class="prt-list-table__summary-card"
              :style="{ gridColumn: `span ${Math.min(INNER_COLS, Math.max(1, item.length))}` }"
            >
              <div class="prt-list-table__summary-card-label">{{ summaryCardLabel(item) }}</div>
              <div class="prt-list-table__summary-card-value">{{ summaryCardValue(item) }}</div>
            </div>
          </template>
        </div>
      </template>
    </div>

    <div v-else-if="!headerOnly" class="prt-list-table__body prt-list-table__body--table" role="region">
      <div
        v-if="tableLoading"
        class="prt-list-table__inline-state"
      >
        {{ trs('itf.passport_runtime_list_table_loading') }}
      </div>
      <div
        v-else-if="tableError"
        class="prt-list-table__inline-state prt-list-table__inline-state--error"
      >
        <span>{{ trs('itf.passport_runtime_list_table_load_error') }}</span>
        <VBtn variant="text" size="small" density="compact" @click="onRetryTable">
          {{ trs('itf.passport_runtime_list_retry') }}
        </VBtn>
      </div>

      <template v-else>
        <div
          class="prt-list-table__thead"
          :style="{ gridTemplateColumns: `repeat(${INNER_COLS}, minmax(0, 1fr))` }"
        >
          <template v-for="(seg, si) in segments" :key="`th-${si}-${seg.kind === 'name' ? 'name' : seg.column.id}`">
            <div
              class="prt-list-table__th"
              :style="{ gridColumn: `span ${seg.span}` }"
            >
              {{ segmentHeaderLabel(seg) }}
            </div>
          </template>
          <div
            class="prt-list-table__th prt-list-table__th--actions"
            :style="{ gridColumn: `span ${ACTIONS_COL_SPAN}` }"
          >
            {{ trs('itf.action') }}
          </div>
        </div>

        <div v-if="!tableRows.length" class="prt-list-table__empty">
          <p class="prt-list-table__empty-title">{{ trs('itf.passport_runtime_list_table_empty') }}</p>
          <p class="prt-list-table__empty-hint">{{ trs('itf.passport_runtime_list_table_empty_hint') }}</p>
        </div>

        <template v-else>
          <div
            v-for="row in paginatedRows"
            :key="`tr-${row.childElementId}`"
            :class="['prt-list-table__tr', rowPendingClass(row.childElementId)]"
            :style="{ gridTemplateColumns: `repeat(${INNER_COLS}, minmax(0, 1fr))` }"
          >
          <template v-for="(seg, si) in segments" :key="`td-${row.childElementId}-${si}-${seg.kind === 'name' ? PB_TABLE_NAME_SEGMENT_ID : seg.column.id}`">
            <div
              class="prt-list-table__td"
              :style="{ gridColumn: `span ${seg.span}` }"
            >
              <RouterLink
                v-if="seg.kind === 'name'"
                class="prt-list-table__name-link"
                :to="childPassportTo(row.childElementId)"
              >
                {{ row.name }}
              </RouterLink>
              <span v-else class="prt-list-table__cell-text">{{ cellTextForSegment(row, seg) }}</span>
              <span
                v-if="seg.kind === 'name' && pendingStateFor(row.childElementId)?.kind === 'link'"
                class="prt-list-table__badge prt-list-table__badge--new"
              >{{ trs('itf.passport_runtime_list_pending_new') }}</span>
              <span
                v-if="seg.kind === 'name' && pendingStateFor(row.childElementId)?.kind === 'unlink'"
                class="prt-list-table__badge prt-list-table__badge--unlink"
              >{{ trs('itf.passport_runtime_list_pending_unlink') }}</span>
            </div>
          </template>
          <div
            class="prt-list-table__td prt-list-table__td--actions"
            :style="{ gridColumn: `span ${ACTIONS_COL_SPAN}` }"
          >
            <VTooltip location="top">
              <template #activator="{ props: tip }">
                <span v-bind="tip" class="prt-list-table__action-wrap">
                  <RouterLink
                    v-if="!fieldsLocked"
                    :to="childPassportTo(row.childElementId)"
                    custom
                    v-slot="{ href, navigate }"
                  >
                    <VBtn
                      icon
                      variant="text"
                      density="compact"
                      :href="href"
                      :aria-label="trs('itf.view')"
                      @click="navigate"
                    >
                      <VIcons type="visibility" />
                    </VBtn>
                  </RouterLink>
                  <VBtn
                    v-else
                    icon
                    variant="text"
                    density="compact"
                    disabled
                    :aria-label="trs('itf.view')"
                  >
                    <VIcons type="visibility" />
                  </VBtn>
                </span>
              </template>
              <span>{{ trs('itf.view') }}</span>
            </VTooltip>

            <VTooltip location="top">
              <template #activator="{ props: tip }">
                <span v-bind="tip" class="prt-list-table__action-wrap">
                  <VBtn
                    icon
                    variant="text"
                    density="compact"
                    :aria-label="mapTooltip(coordsPresent(row.coordinates))"
                    @click="onMapRowAction(row.childElementId, coordsPresent(row.coordinates))"
                  >
                    <VIcons :type="coordsPresent(row.coordinates) ? 'location_on' : 'add_location_alt'" />
                  </VBtn>
                </span>
              </template>
              <span>{{ mapTooltip(coordsPresent(row.coordinates)) }}</span>
            </VTooltip>

            <VTooltip location="top">
              <template #activator="{ props: tip }">
                <span v-bind="tip" class="prt-list-table__action-wrap">
                  <VBtn
                    v-if="pendingStateFor(row.childElementId)"
                    icon
                    variant="text"
                    density="compact"
                    :aria-label="trs('itf.passport_runtime_list_pending_undo')"
                    @click="onUndoPending(pendingStateFor(row.childElementId)?.key)"
                  >
                    <VIcons type="undo" />
                  </VBtn>
                  <VBtn
                    v-else
                    icon
                    variant="text"
                    density="compact"
                    :disabled="fieldsLocked || bindingPending"
                    :aria-label="trs('itf.passport_runtime_list_unlink')"
                    @click="onUnlinkRow(row.childElementId, row.name)"
                  >
                    <VIcons type="link_off" />
                  </VBtn>
                </span>
              </template>
              <span>{{
                pendingStateFor(row.childElementId)
                  ? trs('itf.passport_runtime_list_pending_undo')
                  : trs('itf.passport_runtime_list_unlink')
              }}</span>
            </VTooltip>

            <VTooltip v-if="!pendingStateFor(row.childElementId)" location="top">
              <template #activator="{ props: tip }">
                <span v-bind="tip" class="prt-list-table__action-wrap">
                  <VBtn
                    icon
                    variant="text"
                    density="compact"
                    color="error"
                    :disabled="!canDeleteRow(row.childElementId) || fieldsLocked || bindingPending"
                    :aria-label="trs('itf.archive')"
                    @click="onDeleteRow(row.childElementId, row.name)"
                  >
                    <VIcons type="archive" />
                  </VBtn>
                </span>
              </template>
              <span>{{ deleteTooltip(row.childElementId) }}</span>
            </VTooltip>
          </div>
        </div>
        <div
          v-if="totalRows > 0"
          class="prt-list-table__pagination"
        >
          <div class="prt-list-table__pagination-per-page">
            <span>Записей на странице:</span>
            <VSelect
              v-model="itemsPerPage"
              :items="[10, 20, 50, 100]"
              density="compact"
              variant="outlined"
              hide-details
            />
          </div>
          <div class="prt-list-table__pagination-info">
            {{ paginationStart }}–{{ paginationEnd }} из {{ totalRows }}
          </div>
          <div class="prt-list-table__pagination-nav">
            <VPagination
              v-model="page"
              :length="totalPages"
              density="comfortable"
              rounded
              show-first-last-page
              :total-visible="0"
              variant="plain"
              first-icon="keyboard_double_arrow_left"
              last-icon="keyboard_double_arrow_right"
              prev-icon="chevron_left"
              next-icon="chevron_right"
            />
          </div>
        </div>
        </template>
      </template>
    </div>
    <PassportRuntimeLinkTableModal
      v-if="!headerOnly"
      v-model="linkModalOpen"
      :inventory-group-object-id="objectId"
      :parent-object-list-id="elementId"
      :list-group-id="listGroupId"
      :table-field-id="String(layoutItem.id ?? '')"
      :invent-type-id="inventTypeId"
      :bound-child-ids="boundChildIds"
      @attached="onLinkAttached"
    />
  </div>
</template>

<style scoped>
.prt-list-table {
  width: 100%;
  min-width: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
  background: #fff;
  font-size: 12px;
}

.prt-list-table__header {
  display: flex;
  align-items: center;
  padding: 7px 12px;
  gap: 8px;
  font-weight: 600;
  line-height: 1;
  color: #fff;
  background: #f57c00;
}

.prt-list-table__header-icon {
  flex-shrink: 0;
  opacity: 0.95;
  font-size: 16px;
}

.prt-list-table__header-title {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prt-list-table__header-tools {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
}

.prt-list-table__header-action-wrap {
  display: inline-flex;
}

.prt-list-table__header-tool-btn {
  color: rgba(255, 255, 255, 0.95) !important;
}

.prt-list-table__switch :deep(.v-switch) {
  margin: 0;
}

.prt-list-table__switch :deep(.v-label) {
  color: rgba(255, 255, 255, 0.95);
  font-weight: 600;
  font-size: 12px;
  opacity: 1;
}

.prt-list-table__switch :deep(.v-switch__track) {
  opacity: 1 !important;
  background-color: rgba(0, 0, 0, 0.38) !important;
}

.prt-list-table__body {
  padding: 0;
}

.prt-list-table__body--summary {
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.prt-list-table__summary-empty {
  margin: 0;
  color: rgba(0, 0, 0, 0.6);
}

.prt-list-table__summary-hint {
  margin: 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.prt-list-table__summary-row {
  display: grid;
  gap: 8px;
  align-items: stretch;
}

.prt-list-table__summary-heading {
  font-weight: 600;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.75);
  padding: 4px 0;
}

.prt-list-table__summary-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 8px 10px;
  background: #fafafa;
  min-height: 52px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.prt-list-table__summary-card-label {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
  line-height: 1.25;
}

.prt-list-table__summary-card-value {
  font-size: 13px;
  color: rgba(0, 0, 0, 0.75);
}

.prt-list-table__thead,
.prt-list-table__tr {
  display: grid;
  width: 100%;
}

.prt-list-table__thead {
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
  background: #fafafa;
}

.prt-list-table__tr {
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.prt-list-table__tr:last-child {
  border-bottom: none;
}

.prt-list-table__tr--pending-unlink {
  opacity: 0.5;
}

.prt-list-table__tr--pending-unlink .prt-list-table__name-link {
  text-decoration: line-through;
}

.prt-list-table__tr--pending-link {
  background: rgba(255, 193, 7, 0.08);
}

.prt-list-table__badge {
  margin-inline-start: 6px;
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}

.prt-list-table__badge--new {
  background: rgba(255, 193, 7, 0.25);
  color: #bf360c;
}

.prt-list-table__badge--unlink {
  background: rgba(0, 0, 0, 0.08);
  color: rgba(0, 0, 0, 0.6);
}

.prt-list-table__th {
  padding: 8px 12px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.6);
  text-align: left;
  min-width: 0;
}

.prt-list-table__th--actions {
  text-align: center;
}

.prt-list-table__td {
  padding: 8px 12px;
  min-width: 0;
  color: rgba(0, 0, 0, 0.85);
  display: flex;
  align-items: center;
}

.prt-list-table__td--actions {
  justify-content: center;
  gap: 0;
  flex-wrap: nowrap;
}

.prt-list-table__cell-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
}

.prt-list-table__name-link {
  color: rgb(25, 118, 210);
  text-decoration: none;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prt-list-table__name-link:hover {
  text-decoration: underline;
}

.prt-list-table__action-wrap {
  display: inline-flex;

  .v-btn {
    width: 28px;
    height: 28px;

    .v-icon {
      font-size: 18px;
    }
  }
}

.prt-list-table__empty {
  padding: 16px 12px;
  color: rgba(0, 0, 0, 0.6);
}

.prt-list-table__empty-title {
  margin: 0 0 6px;
  font-weight: 500;
}

.prt-list-table__empty-hint {
  margin: 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}

.prt-list-table__inline-state {
  padding: 10px 12px;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.prt-list-table__inline-state--error {
  color: #b71c1c;
}

.prt-list-table__body--nested {
  padding: 10px 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  background: #fafafa;
}

.prt-list-table__nested-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.prt-list-table__nested-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
}

.prt-list-table__nested-link {
  color: rgb(25, 118, 210);
  text-decoration: none;
  font-size: 13px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.prt-list-table__nested-link:hover {
  text-decoration: underline;
}

.prt-list-table__nested-type {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
  flex-shrink: 0;
  margin-left: 8px;
}

.prt-list-table__pagination {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding: 8px 4px;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  background: #fff;
  font-size: 12px;
}

.prt-list-table__pagination-per-page {
  align-items: center;
  display: flex;
  justify-content: center;
}

.prt-list-table__pagination-per-page > span {
  padding-inline-end: 8px;
  color: rgba(0, 0, 0, 0.6);
}

.prt-list-table__pagination-per-page > .v-select {
  width: 90px;
}

.prt-list-table__pagination-info {
  display: flex;
  justify-content: flex-end;
  min-width: 116px;
  padding: 0 16px;
  color: rgba(0, 0, 0, 0.6);
}

.prt-list-table__pagination-nav {
  align-items: center;
  display: flex;
  margin-inline-start: 16px;
}
</style>
